// ─── Admin numeric text-input helpers ───────────────────────────────────────
//
// The admin numeric inputs are plain text fields (type='text' + inputMode) so
// the Hungarian decimal comma (65,5) can be typed — type='number' silently
// drops comma input. Drafts keep the raw string the admin types; these helpers
// normalize to the API/DB form (dot-decimal string / integer) on save, and
// sanitize pasted values ("72 000 000", "72.000.000", "65,5") on change so a
// paste isn't silently rejected by the input regexes (F-MANUAL-EL0NWR72).

/** Allowed *partial* input while typing — lets through "", "-", "65,", ".5". */
export const DECIMAL_INPUT_RE = /^-?\d*[.,]?\d*$/;
export const INT_INPUT_RE = /^-?\d*$/;

/**
 * Paste-friendly normalizer for integer fields. Typed input passes through
 * untouched; anything else gets spaces (incl. non-breaking) and "." / ","
 * thousand separators stripped, so "72 000 000" and "72.000.000" become
 * "72000000". Truly invalid input still fails INT_INPUT_RE afterwards and
 * must be rejected by the caller.
 */
export function sanitizeInt(raw: string): string {
  if (INT_INPUT_RE.test(raw)) return raw;
  return raw.replace(/\s/g, "").replace(/[.,](?=\d{3}(?:\D|$))/g, "");
}

/**
 * Paste-friendly normalizer for decimal fields: strips spaces and converts
 * the decimal comma to a dot ("65,5" → "65.5"). Typed input passes through
 * untouched so a comma stays visible while typing.
 */
export function sanitizeDecimal(raw: string): string {
  if (DECIMAL_INPUT_RE.test(raw)) return raw;
  return raw.replace(/\s/g, "").replace(/,/g, ".");
}

/** "65,5" → "65.5" (canonical dot-decimal string); empty / invalid → null.
 *  Kept as a string because numeric() columns expect a string (schema.ts). */
export function parseDecimal(v: string): string | null {
  const s = v.replace(/\s+/g, "").replace(/,/g, ".");
  if (s === "") return null;
  const n = Number(s);
  if (!Number.isFinite(n)) return null;
  return String(n);
}

/** "65,5" / "65" → integer; empty / invalid → null. NaN never escapes. */
export function parseIntOrNull(v: string): number | null {
  const s = v.replace(/\s+/g, "").replace(/,/g, ".");
  if (s === "") return null;
  const n = Number(s);
  if (!Number.isFinite(n)) return null;
  return Math.trunc(n);
}
